import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { ColorV2 } from "config/colors";
import { PayoutType } from "model/Tip";
import type { TipDetails } from "model/Tip";
import { formatNumber } from "utils/utils";
import { formatTipDate } from "../../logic/tips.date.logic";
import {
  BankTransferDetails,
  BankTransferFooter,
  BankTransferLabel,
  BankTransferPrimaryButtonWrap,
  BankTransferValue,
} from "./tips.bankTransferModal.styled";

const MARK_AS_PAID_BUTTON_STYLES = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

interface MarkAsPaidOutParams {
  readonly companyTipIds: readonly number[];
  readonly payoutType: PayoutType;
}

interface TipsBankTransferModalProps {
  readonly isOpen: boolean;
  readonly isSaving: boolean;
  readonly tips: readonly TipDetails[];
  readonly onClose: () => void;
  readonly onMarkAsPaidOut: (params: MarkAsPaidOutParams) => Promise<void>;
}

export const TipsBankTransferModal = (
  props: TipsBankTransferModalProps,
): JSX.Element | null => {
  const { i18n, t } = useTranslation();

  if (!props.isOpen || props.tips.length === 0) return null;

  const totalAmount = props.tips.reduce((sum, tip) => sum + tip.amount, 0);
  const recipientsCount = props.tips.reduce(
    (sum, tip) => sum + tip.workers.length,
    0,
  );
  const tipsPeriod =
    props.tips.length === 1
      ? formatTipDate({
          dateValue: props.tips[0].date,
          isYearVisible: true,
          locale: i18n.language,
        })
      : t("Tips.SelectedTipsPeriod", { count: props.tips.length });

  const handleMarkAsPaidOut = async (): Promise<void> => {
    await props.onMarkAsPaidOut({
      companyTipIds: props.tips.map((tip) => tip.companyTipId),
      payoutType: PayoutType.Manual,
    });
  };

  return (
    <Modal
      label={t("Tips.BankTransfer")}
      size="small"
      isOpen={props.isOpen}
      onClose={props.onClose}
      allowContentOverflow
      footer={
        <BankTransferFooter>
          <SecondaryButton
            label={t("Tips.Cancel")}
            onClick={props.onClose}
            size="small"
            type="button"
          />
          <BankTransferPrimaryButtonWrap>
            <PrimaryButton
              iconName="v2/checkmark"
              isLoading={props.isSaving}
              label={t("Tips.MarkAsPaidOut")}
              onClick={() => {
                void handleMarkAsPaidOut();
              }}
              size="small"
              styleOverrides={MARK_AS_PAID_BUTTON_STYLES}
              type="button"
            />
          </BankTransferPrimaryButtonWrap>
        </BankTransferFooter>
      }
    >
      <BankTransferDetails>
        <BankTransferLabel>{t("Tips.Method")}</BankTransferLabel>
        <BankTransferValue>{t("Tips.BankTransfer")}</BankTransferValue>

        <BankTransferLabel>{t("Tips.TipsPeriod")}</BankTransferLabel>
        <BankTransferValue>{tipsPeriod}</BankTransferValue>

        <BankTransferLabel>{t("Tips.Recipients")}</BankTransferLabel>
        <BankTransferValue>{recipientsCount}</BankTransferValue>

        <BankTransferLabel>
          {t("Tips.TotalAmountToBePaidOut")}
        </BankTransferLabel>
        <BankTransferValue>
          {formatNumber(totalAmount)} {t("Tips.Currency")}
        </BankTransferValue>

        {/* TODO: Re-enable bank file generation controls when the payout flow can create and download bank files. */}
        {/*
        <BankTransferLabel>
          {t("Tips.CreateAndDownloadBankFile")}
        </BankTransferLabel>
        <BankTransferFieldSlot>
          <Toggle
            aria-label={t("Tips.CreateAndDownloadBankFile")}
            checked={shouldCreateBankFile}
            onChange={setShouldCreateBankFile}
            size="compact"
          />
        </BankTransferFieldSlot>

        <BankTransferLabel>{t("Tips.PayoutDateBankFile")}</BankTransferLabel>
        <BankTransferFieldSlot>
          <DatePicker
            dateFormat="DD/MM/YYYY"
            disabled={!shouldCreateBankFile}
            onChange={setPayoutDate}
            size="compact"
            value={payoutDate}
          />
        </BankTransferFieldSlot>
        */}
      </BankTransferDetails>
    </Modal>
  );
};
